import styles from './ListItemMenuOption.module.scss';

import {
  KeyboardEvent,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { MenuContext } from './MenuContext';
import { MenuActionsContext } from './MenuActionsContext';

interface Props {
  children: string;
  onClick?: (actions: { close: () => void; open: () => void }) => void;
  disabled?: boolean;
}

export function ListItemMenuOption(props: Props) {
  const { disabled = false } = props;
  const [focused, setFocused] = useState<boolean>(false);
  const id = useId();
  const context = useContext(MenuContext);
  const actions = useContext(MenuActionsContext);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    context.set(id, { ref, disabled, focused });
  }, [context, id, disabled, focused]);

  useEffect(() => {
    if (context.get(id)?.focused) {
      ref.current?.focus();
    }
  }, [context, id]);

  // Events
  const onBlur = () => {
    setFocused(false);
  };
  const onFocus = () => {
    setFocused(true);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        ref.current?.click();
        break;

      default:
        break;
    }
  };

  const onClick = () => {
    if (disabled) {
      return;
    }
    props.onClick?.({ ...actions });
  };

  const className = [
    styles['list-item-menu-option'],
    props.disabled && styles['list-item-menu-option-disabled'],
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={ref}
      aria-disabled={props.disabled}
      className={className}
      onBlur={onBlur}
      onClick={onClick}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      role="menuitem"
      tabIndex={props.disabled ? undefined : 0}
    >
      {props.children}
    </div>
  );
}
